import React from 'react';
import { Col, Row } from 'reactstrap';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import { Input } from 'edozvola-frontend-lib/dist/ui/components';
import {
  fetchCommunitiesWithCityMunicipalitiesAndCountyQuery,
  fetchCountriesQuery,
  fetchHouseNumbersQuery,
  fetchStreetsQuery,
} from 'app/edozvola/codebooks/codebooks.queries';
import { CROATIA_COUNTRY_VALUE } from 'app/config/constants';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { useFormikContext } from 'formik';
import { fetchCommunityPostalForCommunity } from 'app/edozvola/codebooks/codebooks.service';

const AddressInput = ({ field, errors, setFieldValue, readOnly = false, showClearButton = false }) => {
  const { data: countryOptions, isLoading: countryOptionsLoading } = fetchCountriesQuery();
  const { data: communitiesOptions, isLoading: communitiesOptionsLoading } = fetchCommunitiesWithCityMunicipalitiesAndCountyQuery();
  const { data: streetOptions, isLoading: streetOptionsLoading } = fetchStreetsQuery(
    field?.community ? field?.community[0]?.id : undefined,
  );
  const { data: houseNumberOptions, isLoading: houseNumberOptionsLoading } = fetchHouseNumbersQuery(
    field?.street ? field?.street[0]?.id : undefined,
  );

  const formik = useFormikContext<any>();

  const onCountryChange = (selected: any) => {
    if (selected[0]?.value === CROATIA_COUNTRY_VALUE) {
      setFieldValue('communityString', undefined);
      setFieldValue('streetString', undefined);
      setFieldValue('houseNumberString', undefined);
    } else {
      setFieldValue('community', []);
      setFieldValue('street', []);
      setFieldValue('houseNumber', []);
    }
    setFieldValue('postalCode', '');
    setFieldValue('postName', '');

    setFieldValue('country', selected);
  };

  const onCommunityChange = (selected: any) => {
    setFieldValue('street', []);
    setFieldValue('houseNumber', []);
    setFieldValue('community', selected);

    if (selected?.length > 0) {
      fetchCommunityPostalForCommunity(selected[0].id).then(value => {
        setFieldValue('postalCode', value.postalCode);
        setFieldValue('postName', value.postalCommunity);

        setTimeout(() => {
          formik.validateForm();
        }, 0);
      });
    }
    else {
      setFieldValue('postalCode', '');
      setFieldValue('postName', '');
    }

    setTimeout(() => {
      formik.validateForm();
    }, 0);
  };

  const onStreetChange = (selected: any) => {
    setFieldValue('houseNumber', []);
    setFieldValue('street', selected);

    setTimeout(() => {
      formik.validateForm();
    }, 0);
  };

  const selectedCountryCroatia = () => field?.country && field.country[0]?.value === CROATIA_COUNTRY_VALUE;

  const streetDisabled = !field?.community || field?.community.length === 0;
  const houseNumberDisabled = !field?.street || field?.street.length === 0;

  const postalCodeChanged = (event: any) => {
    setFieldValue(`postalCode`, event.target.value);
  };

  return (
    <>
      <Row>
        <Col md={6}>
          <Label labelText={'Država'} />
          <PaginatedDropdown
            placeholder={!readOnly && 'Odaberi državu'}
            onItemSelect={onCountryChange}
            selected={field?.country}
            labelKey={'value'}
            options={countryOptionsLoading ? [] : countryOptions}
            error={(errors?.country as string) ?? ''}
            disabled={readOnly}
            showErrorMessage
            clearButton={showClearButton}
          />
        </Col>
        <Col md={6}>
          <Label labelText={'Naselje'} />
          {selectedCountryCroatia() ? (
            <PaginatedDropdown
              placeholder={!readOnly && 'Odaberi naselje'}
              onItemSelect={onCommunityChange}
              selected={field?.community}
              labelKey={'value'}
              selectedLabelKey={'displayName'}
              options={communitiesOptionsLoading ? [] : communitiesOptions ?? []}
              disabled={readOnly}
              error={errors && errors.community ? (errors.community as string) : ''}
              showErrorMessage
              clearButton={showClearButton}
            />
          ) : (
            <Input
              value={field?.communityString}
              disabled={readOnly}
              error={errors && errors.communityString ? errors.communityString : ''}
              onChange={event => setFieldValue('communityString', event.target.value)}
            />
          )}
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Label labelText={'Poštanski broj'} />
          <Input
            value={field?.postalCode}
            disabled={readOnly || selectedCountryCroatia()}
            maxLength={15}
            error={errors && errors.postalCode ? errors.postalCode : ''}
            onChange={postalCodeChanged}
          />
        </Col>
        <Col md={6}>
          <Label labelText={'Pošta'} />
          <Input
            value={field?.postName}
            disabled={readOnly || selectedCountryCroatia()}
            maxLength={255}
            error={errors && errors.postName ? errors.postName : ''}
            onChange={e => setFieldValue('postName', e.target.value)}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Label labelText={'Ulica'} />
          {selectedCountryCroatia() ? (
            <PaginatedDropdown
              placeholder={!readOnly && 'Odaberi ulicu'}
              onItemSelect={onStreetChange}
              selected={field?.street}
              labelKey={'value'}
              options={streetOptionsLoading ? [] : streetOptions ?? []}
              disabled={readOnly || streetDisabled}
              error={errors && errors.street && !streetDisabled ? (errors.street as string) : ''}
              showErrorMessage
              clearButton={showClearButton}
            />
          ) : (
            <Input
              value={field?.streetString}
              error={errors && errors.streetString ? errors.streetString : ''}
              disabled={readOnly}
              onChange={event => setFieldValue('streetString', event.target.value)}
            />
          )}
        </Col>
        <Col md={6}>
          <Label labelText={'Kućni broj'} />
          {selectedCountryCroatia() ? (
            <PaginatedDropdown
              placeholder={!readOnly && 'Odaberi kućni broj'}
              onItemSelect={selected => setFieldValue('houseNumber', selected)}
              selected={field?.houseNumber}
              labelKey={'value'}
              options={houseNumberOptionsLoading ? [] : houseNumberOptions ?? []}
              disabled={readOnly || !field?.street || field?.street.length === 0}
              clearButton={showClearButton}
              error={errors && errors.houseNumber && !houseNumberDisabled ? (errors.houseNumber as string) : ''}
              showErrorMessage
            />
          ) : (
            <Input
              value={field?.houseNumberString}
              error={errors && errors.houseNumberString ? errors.houseNumberString : ''}
              disabled={readOnly}
              onChange={event => setFieldValue('houseNumberString', event.target.value)}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

export default AddressInput;
